// AIT-268 — "Solutions" nav panel. Desktop renders a dropdown card under the
// trigger; mobile renders the same rows as an accordion inside the existing
// hamburger sheet. DESIGN.md: dark card surface (#181A1B), 20px radius on the
// card, dual shadow, Meta Blue reserved for the icon badge.
//
// Analytics: CtaClickedProps.cta_location is a closed union
// ('nav' | 'hero' | 'pricing' | 'final_cta'), so rows report 'nav' and carry
// their identity in cta_text. Do not widen the shared registry for this.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, Instagram, MessageCircle } from 'lucide-react';

import { emit } from '../lib/analytics';
import { CHANNEL_LABEL } from '../lib/channel-copy';

interface SolutionRow {
  to: string;
  label: string;
  Icon: typeof MessageCircle;
}

export const SOLUTIONS: SolutionRow[] = [
  {
    to: '/whatsapp',
    label: CHANNEL_LABEL.whatsapp,
    Icon: MessageCircle,
  },
  {
    to: '/instagram',
    label: CHANNEL_LABEL.instagram,
    Icon: Instagram,
  },
];

function trackRow(label: string, to: string) {
  emit('cta_clicked', {
    cta_location: 'nav',
    cta_text: `Solutions: ${label}`,
    destination_url: to,
  });
}

function SolutionLink({
  row,
  onNavigate,
}: {
  row: SolutionRow;
  onNavigate: () => void;
}) {
  const { to, label, Icon } = row;
  return (
    <Link
      to={to}
      onClick={() => {
        trackRow(label, to);
        onNavigate();
      }}
      className="flex items-center gap-3 rounded-[14px] px-3 py-3 transition hover:bg-white/[0.04]"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-[#0064E0]/40 bg-[#0064E0]/15 text-[#3B86FF]">
        <Icon className="size-4" />
      </span>
      <span className="text-sm font-semibold text-white">{label}</span>
    </Link>
  );
}

export function NavSolutionsDesktop() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Escape + outside-pointerdown close. Same approach as the mobile sheet in
  // nav.tsx: the nav's backdrop-blur makes it the containing block for fixed
  // descendants, so a full-viewport overlay would not cover the page.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <div
      className="relative hidden md:block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Click and focus OPEN, they do not toggle: onMouseEnter has already
          set open=true by the time a mouse click lands, so a toggle would
          close the panel the user just pointed at. Escape, outside-pointerdown
          and mouseleave are the close paths. min-h-11 keeps the 44px touch
          target DESIGN.md requires. */}
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls="solutions-panel"
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        className="inline-flex min-h-11 items-center gap-1 hover:text-white transition"
      >
        Solutions
        <ChevronDown
          aria-hidden="true"
          className={`size-4 transition ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div
          ref={panelRef}
          id="solutions-panel"
          className="absolute left-0 top-full z-[70] w-[340px] pt-3"
        >
          {/* No group heading: a "By Channel" label only earns its space once
              the panel has a second column to distinguish it from. */}
          <div className="rounded-[20px] border border-white/[0.08] bg-[#181A1B] p-3 shadow-[0_16px_40px_rgba(0,0,0,0.55),0_4px_12px_rgba(0,0,0,0.35)]">
            {SOLUTIONS.map((row) => (
              <SolutionLink
                key={row.to}
                row={row}
                onNavigate={() => setOpen(false)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function NavSolutionsMobile({ onNavigate }: { onNavigate: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="solutions-mobile-panel"
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-11 items-center justify-between px-6 py-3 text-left text-white/80 transition hover:bg-white/[0.04] hover:text-white"
      >
        Solutions
        <ChevronDown
          aria-hidden="true"
          className={`size-4 transition ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div id="solutions-mobile-panel" className="px-3 pb-1">
          {SOLUTIONS.map((row) => (
            <SolutionLink key={row.to} row={row} onNavigate={onNavigate} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
